import React, { useEffect, useState, useCallback, useRef } from 'react';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';

// Custom centered loading overlay component
const CenteredLoadingOverlay = () => (
    <Box
        sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            zIndex: 1,
        }}
    >
        <CircularProgress size={60} />
    </Box>
);

const PlayerStatsTable = ({ apiUrl, careerStats = false }) => {
    const [playerStats, setPlayerStats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    // Pagination state
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 100,
    });
    const [rowCount, setRowCount] = useState(0);

    // Sorting state
    const [sortModel, setSortModel] = useState([
        { field: 'war', sort: 'desc' }
    ]);

    // State for filters
    const [selectedStatType, setSelectedStatType] = useState('ALL');
    const [selectedYear, setSelectedYear] = useState('');
    const [majorsFilter, setMajorsFilter] = useState('true');
    const [seasonsFilter, setSeasonsFilter] = useState('Regular Season');

    // State for dropdown options
    const [statTypeOptions, setStatTypeOptions] = useState(['ALL', 'HITTING', 'PITCHING']);

    // Ref to track if initial load is complete
    const initialLoadComplete = useRef(false);

    // Build query parameters for API call
    const buildQueryParams = useCallback(() => {
        const params = new URLSearchParams();
        
        // Pagination
        params.append('page', paginationModel.page + 1);
        params.append('page_size', paginationModel.pageSize);
        params.append('career_stats', careerStats.toString());
        
        // Sorting
        if (sortModel.length > 0) {
            const sort = sortModel[0];
            params.append('sort_by', sort.field);
            params.append('sort_order', sort.sort);
        }
        
        // Search
        if (searchTerm) {
            params.append('player_name', searchTerm);
        }
        
        // Filters
        if (selectedStatType === 'ALL') {
            // When ALL is selected, explicitly request only HITTING and PITCHING
            params.append('stat_type', 'HITTING');
            params.append('stat_type', 'PITCHING');
        } else if (selectedStatType) {
            params.append('stat_type', selectedStatType);
        }
        if (selectedYear) {
            console.log('Adding year parameter:', selectedYear);
            params.append('year', selectedYear);
        }
        if (majorsFilter && majorsFilter !== 'All') params.append('majors', majorsFilter);
        if (seasonsFilter && seasonsFilter !== 'All') {
            if (seasonsFilter === 'Regular Season') {
                params.append('seasons', 'REGULAR');
            } else if (seasonsFilter === 'Postseason') {
                params.append('seasons', 'POST');
            }
        }
        
        console.log('Built query params:', params.toString());
        return params;
    }, [paginationModel, sortModel, searchTerm, selectedStatType, selectedYear, majorsFilter, seasonsFilter, careerStats]);

    // Fetch data with server-side pagination
    const fetchData = useCallback(async () => {
        setLoading(true);
        // Clear current data immediately when starting to fetch
        setPlayerStats([]);
        
        try {
            const params = buildQueryParams();
            const url = `${apiUrl}?${params.toString()}`;
            
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            
            // Handle the API response format with pagination
            const stats = data.data || data.results || data;
            
            // Extract pagination info from the response
            const paginationInfo = data.pagination || {};
            const totalCount = paginationInfo.total_count || data.total || data.count || data.total_count || stats.length;
            
            const statsWithIds = stats.map((stat, index) => ({
                ...stat,
                id: stat.hash_id || stat.uid || index,
                // Extract player info if available
                player_name: stat.name,
                player_url: stat.url || null,
                username: stat.player?.user?.username || 'N/A',
                user_url: stat.player?.user?.url || null,
                team_name: stat.player?.team?.team_name || 'N/A',
                league_name: stat.player?.team?.division?.conference?.league?.league_name || 'N/A',
            }));
            
            setPlayerStats(statsWithIds);
            setRowCount(totalCount);
            setLoading(false);
        } catch (error) {
            console.error("Error fetching data:", error);
            setError(error);
            setLoading(false);
        }
    }, [apiUrl, buildQueryParams]);

    // Single useEffect for initial load
    useEffect(() => {
        const initializeData = async () => {
            await fetchData();
            initialLoadComplete.current = true;
        };
        initializeData();
    }, []); // Only run once on mount

    // Separate useEffect for data refetching when filters change
    useEffect(() => {
        // Only refetch if initial load is complete
        if (initialLoadComplete.current) {
            fetchData();
        }
    }, [fetchData]);

    const getStatTypeColor = (statType) => {
        switch (statType) {
            case 'HITTING': return 'success';
            case 'PITCHING': return 'error';
            case 'FIELDING': return 'info';
            default: return 'default';
        }
    };

    const columns = [
        {
            field: 'name', // Using 'name' field for sorting
            headerName: 'Player Name',
            width: 200,
            sortable: true,
            renderCell: (params) => (
                params.row.player_url ? (
                    <Link href={params.row.player_url} target="_blank" rel="noopener">
                        {params.value}
                    </Link>
                ) : params.value
            ),
        },
        {
            field: 'stat_type',
            headerName: 'Stat Type',
            width: 120,
            sortable: true,
            renderCell: (params) => (
                <Chip 
                    label={params.value} 
                    color={getStatTypeColor(params.value)}
                    size="small"
                    variant="outlined"
                />
            ),
        },
        ...(careerStats ? [] : [
            { 
                field: 'year', 
                headerName: 'Year', 
                type: 'number', 
                width: 80,
                sortable: true,
                valueFormatter: (params) => {
                    return params.value;
                },
            },
            { 
                field: 'age', 
                headerName: 'Age', 
                type: 'number', 
                width: 80,
                sortable: true,
            },
        ]),
        { 
            field: 'games', 
            headerName: 'Games', 
            type: 'number', 
            width: 100,
            sortable: true,
        },
        { 
            field: 'war', 
            headerName: 'WAR', 
            type: 'number', 
            width: 100,
            sortable: true,
        },
        {
            field: 'majors',
            headerName: 'Majors',
            width: 100,
            sortable: true,
            renderCell: (params) => (
                <Chip 
                    label={params.value ? 'Yes' : 'No'} 
                    color={params.value ? 'success' : 'default'}
                    size="small"
                    variant="outlined"
                />
            ),
        },
        {
            field: 'postseason',
            headerName: 'Seasons',
            width: 180,
            sortable: true,
            renderCell: (params) => {
                const isPostseason = params.value;
                return (
                    <Chip 
                        label={isPostseason ? 'Postseason' : 'Regular Season'} 
                        color={isPostseason ? 'warning' : 'default'}
                        size="small"
                        variant="outlined"
                    />
                );
            },
        },
    ];

    if (error) {
        return <div>Error: {error.message}</div>;
    }

    const createDropdown = (label, value, onChange, options) => {
        const labelId = `${label.toLowerCase().replace(/\s+/g, '-')}-select-label`;

        console.log(`Creating dropdown for ${label}:`, { value, options });

        // Special handling for stat type options to show proper display names
        const getDisplayOptions = () => {
            if (label === 'Stat Type') {
                return [
                    { value: 'ALL', label: 'All Stats' },
                    { value: 'HITTING', label: 'Hitters' },
                    { value: 'PITCHING', label: 'Pitchers' }
                ];
            } else if (label === 'Majors') {
                return [
                    { value: 'All', label: 'All' },
                    { value: 'true', label: 'Majors' },
                    { value: 'false', label: 'Minors' }
                ];
            } else if (label === 'Seasons') {
                return [
                    { value: 'All', label: 'All' },
                    { value: 'Postseason', label: 'Postseason' },
                    { value: 'Regular Season', label: 'Regular Season' }
                ];
            } else if (label === 'Year') {
                // Convert years to strings for the dropdown and add "All Years" option
                const yearOptions = [
                    { value: '', label: 'All Years' },
                    ...options.map(year => ({ value: year.toString(), label: year.toString() }))
                ];
                return yearOptions;
            }
            return options.map(opt => ({ value: opt, label: opt }));
        };

        const displayOptions = getDisplayOptions();
        console.log(`Display options for ${label}:`, displayOptions);

        return (
            <Grid item xs={12} sm={6} md={4} lg={3}>
                <FormControl fullWidth variant="outlined">
                    <InputLabel id={labelId} shrink={true}>
                        {label === 'Majors' ? 'Leagues' : label === 'Seasons' ? 'Seasons' : label}
                    </InputLabel>
                    <Select
                        labelId={labelId}
                        displayEmpty
                        value={value}
                        onChange={onChange}
                        label={label === 'Majors' ? 'Leagues' : label === 'Seasons' ? 'Seasons' : label}
                        sx={{ width: '100%' }}
                    >
                        {label === 'Stat Type' || label === 'Majors' || label === 'Seasons' || label === 'Year' ? (
                            displayOptions.map(opt => (
                                <MenuItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                </MenuItem>
                            ))
                        ) : (
                            <>
                                <MenuItem value="">
                                    <em>All {label}s</em>
                                </MenuItem>
                                {options.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                            </>
                        )}
                    </Select>
                </FormControl>
            </Grid>
        );
    };

    return (
        <div>
            <Box sx={{ mb: 2 }}>
                <h2 style={{ margin: '0 0 16px 0', fontSize: '24px', fontWeight: '600' }}>
                    {careerStats ? 'Career' : 'Season'} Player Stats
                </h2>
            </Box>
            <Box sx={{ mb: 2, p: 2, border: '1px solid lightgray', borderRadius: '4px' }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} lg={6}>
                        <TextField
                            label="Player name"
                            variant="outlined"
                            fullWidth
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                        <TextField
                            label="Year"
                            variant="outlined"
                            fullWidth
                            value={selectedYear}
                            onChange={(e) => {
                                console.log('Year input changed:', e.target.value);
                                setSelectedYear(e.target.value);
                            }}
                            placeholder="e.g., 2024"
                        />
                    </Grid>
                    {createDropdown("Stat Type", selectedStatType, (e) => setSelectedStatType(e.target.value), statTypeOptions)}
                    {createDropdown("Majors", majorsFilter, (e) => setMajorsFilter(e.target.value), ['All', 'true', 'false'])}
                    {createDropdown("Seasons", seasonsFilter, (e) => setSeasonsFilter(e.target.value), ['All', 'Postseason', 'Regular Season'])}
                </Grid>
            </Box>
            
            <div style={{ height: 700, width: '100%' }}>
                <DataGrid
                    rows={playerStats}
                    columns={columns}
                    pagination
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    pageSizeOptions={[25, 50, 100]}
                    rowCount={rowCount}
                    paginationMode="server"
                    sortingMode="server"
                    sortModel={sortModel}
                    onSortModelChange={setSortModel}
                    loading={loading}
                    disableSelectionOnClick
                    slots={{
                        toolbar: GridToolbar,
                        loadingOverlay: CenteredLoadingOverlay,
                    }}
                />
            </div>
        </div>
    );
};

export default PlayerStatsTable; 